import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import {
  MyHrSystemSettingsLoginTwoFactorCard,
  MyHrSystemSettingsCompanySelectorCard,
} from "my-hr/components";
import { MyHrSSOForm } from "my-hr/forms";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcSettingSwitch,
} from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import {
  useOcClientState,
  useOcIsMobile,
  useOcProperty,
  useOcSnackbar,
  useOcTextInput,
} from "oc/hooks";

export const MyHrSystemSettingsLoginCard = () => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const { setOpenMessage } = useOcSnackbar();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: loginType } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  const [loginTypeTempState, setLoginTypeTempState] = useOcClientState(
    "loginTypeTempState",
    loginType,
  );

  const { data: passwordPolicy } = useOcProperty({
    resource: "system",
    key: "passwordPolicy",
  });

  const [passwordPolicyTempState, setPasswordPolicyTempState] =
    useOcClientState("passwordPolicyTempState", passwordPolicy || "weak");

  useEffect(() => {
    if (passwordPolicy && passwordPolicyTempState !== passwordPolicy) {
      setPasswordPolicyTempState(passwordPolicy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordPolicy]);

  const handlePasswordPolicyChange = () => {
    const newState = passwordPolicyTempState === "strong" ? "weak" : "strong";
    setPasswordPolicyTempState(newState);
  };

  const handleLoginTypeChange = () => {
    const newState = loginTypeTempState === "sso" ? "legacy" : "sso";
    setLoginTypeTempState(newState);
  };

  const { data: passwordVisibility } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const isPasswordVisible = passwordVisibility === "true" ? true : false;

  const [passwordVisibilityTempState, setPasswordVisibilityTempState] =
    useState(isPasswordVisible);

  const ssoAddress = useOcTextInput({ optional: false, defaultValue: "" });
  const ssoPort = useOcTextInput({ optional: true, defaultValue: "" });
  const ssoCN = useOcTextInput({ optional: false, defaultValue: "" });
  const domainSuffix = useOcTextInput({ optional: true, defaultValue: "" });
  const systemUserName = useOcTextInput({ optional: true, defaultValue: "" });
  const systemUserPassword = useOcTextInput({
    optional: true,
    defaultValue: "",
  });

  const { data: ldapAddress } = useOcProperty({
    resource: "system",
    key: "ldapAddress",
    onCompleted: ({ value }) => {
      if (value) {
        ssoAddress.setValue(value);
      }
    },
  });

  const { data: ldapPort } = useOcProperty({
    resource: "system",
    key: "ldapPort",
    onCompleted: ({ value }) => {
      if (value) {
        ssoPort.setValue(value);
      }
    },
  });

  const { data: ldapCN } = useOcProperty({
    resource: "system",
    key: "ldapCN",
    onCompleted: ({ value }) => {
      if (value) {
        ssoCN.setValue(value);
      }
    },
  });

  const { data: ldapDomainSuffix } = useOcProperty({
    resource: "system",
    key: "ldapDomainSuffix",
    onCompleted: ({ value }) => {
      if (value) {
        domainSuffix.setValue(value);
      }
    },
  });

  const { data: ldapSystemUserName } = useOcProperty({
    resource: "system",
    key: "ldapSystemUserName",
    onCompleted: ({ value }) => {
      if (value) {
        systemUserName.setValue(value);
      }
    },
  });

  const { data: ldapSystemUserPassword } = useOcProperty({
    resource: "system",
    key: "ldapSystemUserPassword",
    onCompleted: ({ value }) => {
      if (value) {
        systemUserPassword.setValue(value);
      }
    },
  });

  let passwordPolicyInfoText =
    passwordPolicyTempState === "strong"
      ? t("MY_HR_INFO_TEXT_STRONG_PASSWORD")
      : t("MY_HR_INFO_TEXT_NOT_STRONG_PASSWORD");

  const [updateProperty] = useMutation(OC_UPDATE_PROPERTY, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
      });
    },
    onError({ ...err }) {
      alert("error" + err.toString());
      setOpenMessage({
        type: "error",
        message: t("OC_PROPERTY_SAVE_FAILED"),
      });
    },
  });

  const handleSave = () => {
    if (loginTypeTempState === "legacy") {
      updateProperty({
        variables: {
          resource: "system",
          key: "loginType",
          value: loginTypeTempState,
        },
      });
      if (passwordPolicy !== passwordPolicyTempState) {
        const newState = passwordPolicy === "strong" ? "weak" : "strong";
        updateProperty({
          variables: {
            resource: "system",
            key: "passwordPolicy",
            value: newState,
          },
        });
      }
    }

    if (loginTypeTempState === "sso") {
      updateProperty({
        variables: {
          resource: "system",
          key: "loginType",
          value: loginTypeTempState,
        },
      });

      if (!ssoAddress.isValid()) ssoAddress.validate();
      if (!ssoPort.isValid()) ssoPort.validate();

      updateProperty({
        variables: {
          resource: "system",
          key: "ldapAddress",
          value: ssoAddress.value,
        },
      });
      updateProperty({
        variables: {
          resource: "system",
          key: "ldapPort",
          value: ssoPort.value,
        },
      });
      updateProperty({
        variables: { resource: "system", key: "ldapCN", value: ssoCN.value },
      });
      updateProperty({
        variables: {
          resource: "system",
          key: "ldapDomainSuffix",
          value: domainSuffix.value,
        },
      });
      updateProperty({
        variables: {
          resource: "system",
          key: "ldapSystemUserName",
          value: systemUserName.value,
        },
      });
      updateProperty({
        variables: {
          resource: "system",
          key: "ldapSystemUserPassword",
          value: systemUserPassword.value,
        },
      });
    }

    if (passwordVisibility !== passwordVisibilityTempState) {
      updateProperty({
        variables: {
          resource: "system",
          key: "passwordVisibility",
          value: passwordVisibilityTempState.toString(),
        },
      });
    }

    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    if (passwordPolicy !== passwordPolicyTempState) {
      setPasswordPolicyTempState(passwordPolicy);
    }
    if (loginTypeTempState !== loginType) {
      setLoginTypeTempState(loginType);
    }
    if (isPasswordVisible !== passwordVisibilityTempState) {
      setPasswordVisibilityTempState(isPasswordVisible);
    }
  };

  let disabled = true;

  if (passwordPolicy !== passwordPolicyTempState) {
    disabled = false;
  }
  if (loginTypeTempState !== loginType) {
    disabled = false;
  }
  if (isPasswordVisible !== passwordVisibilityTempState) {
    disabled = false;
  }
  if (
    loginType === "sso" &&
    (ldapAddress !== ssoAddress.value ||
      ldapPort !== ssoPort.value ||
      ldapCN !== ssoCN.value ||
      ldapDomainSuffix !== domainSuffix.value ||
      ldapSystemUserName !== systemUserName.value ||
      ldapSystemUserPassword !== systemUserPassword.value)
  ) {
    disabled = false;
  }

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  useEffect(() => {
    setPasswordVisibilityTempState(isPasswordVisible);
  }, [isPasswordVisible]);

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_HR_SIGN_IN")}>
        <Box>
          <OcSettingSwitch
            id="single-sign-on-switch"
            label={t("MY_HR_DIRECTORY")}
            info={t("MY_HR_INFO_TEXT_DIRECTORY")}
            value={loginTypeTempState === "sso"}
            onChange={handleLoginTypeChange}
            isMobile={isMobile}
            disabled={false}
          />
          <Box height="28px" />
          {loginTypeTempState === "sso" ? (
            <Box>
              <MyHrSSOForm
                ssoAddress={ssoAddress}
                ssoPort={ssoPort}
                ssoCN={ssoCN}
                domainSuffix={domainSuffix}
                systemUserName={systemUserName}
                systemUserPassword={systemUserPassword}
                isMobile={isMobile}
              />
              <Box height="28px" />
              <OcSettingSwitch
                id="password-visibility-info-switch"
                label={t("MY_HR_PASSWORD_VISIBILITY")}
                info={t("MY_HR_PASSWORD_VISIBILITY_INFO")}
                value={passwordVisibilityTempState}
                onChange={(e) =>
                  setPasswordVisibilityTempState(e.target.checked)
                }
                isMobile={isMobile}
                disabled={false}
              />
            </Box>
          ) : (
            <Box>
              <OcSettingSwitch
                id="password-policy-info-switch"
                label={t("MY_HR_USING_STRONG_PASSWORD")}
                info={passwordPolicyInfoText}
                value={passwordPolicyTempState === "strong"}
                onChange={handlePasswordPolicyChange}
                isMobile={isMobile}
                disabled={false}
              />
              <Box height="28px" />
              <OcSettingSwitch
                id="password-visibility-info-switch"
                label={t("MY_HR_PASSWORD_VISIBILITY")}
                info={t("MY_HR_PASSWORD_VISIBILITY_INFO")}
                value={passwordVisibilityTempState}
                onChange={(e) =>
                  setPasswordVisibilityTempState(e.target.checked)
                }
                isMobile={isMobile}
                disabled={false}
              />
            </Box>
          )}
          <OcCardButtonBar
            handleRightButton={handleCancel}
            rightButtonColor="secondary"
            rightButtonText={t("OC_CANCEL")}
            rightButtonId="cancel-login-settings"
            handlePrimaryButton={() => setShowConfirmDialog(true)}
            primaryButtonText={t("OC_SAVE")}
            primaryButtonDisabled={disabled}
            primaryButtonId="save-login-settings"
          />
        </Box>
      </OcCard>
      <Box height="16px" />
      <MyHrSystemSettingsLoginTwoFactorCard />
      <Box height="16px" />
      <MyHrSystemSettingsCompanySelectorCard />
    </>
  );
};

export default MyHrSystemSettingsLoginCard;
